import { BaseService } from "@/services/BaseService";
import type {
  AdminAccessGroupFilterOptions,
  AccessGroupListQuery,
  AdminAccessGroupDetail,
  AdminAccessGroupSummary,
  CreateAdminAccessGroupDto,
  PaginatedAdminAccessGroupResponse,
  UpdateAccessGroupDto,
  UserAdminAccessGroupSummary,
} from "@/types/access-group";

class AdminAccessGroupsService extends BaseService {
  private readonly basePath = "/admin/access-groups";

  getAccessGroups(
    query?: AccessGroupListQuery,
  ): Promise<PaginatedAdminAccessGroupResponse> {
    return this.get<PaginatedAdminAccessGroupResponse>(this.basePath, query, {
      skipSelectedHotelHeader: true,
    });
  }

  getAccessGroupFilterOptions(): Promise<AdminAccessGroupFilterOptions> {
    return this.get<AdminAccessGroupFilterOptions>(
      `${this.basePath}/filter-options`,
      undefined,
      {
        skipSelectedHotelHeader: true,
      },
    );
  }

  getAccessGroupById(id: string): Promise<AdminAccessGroupDetail> {
    return this.get<AdminAccessGroupDetail>(
      `${this.basePath}/${id}`,
      undefined,
      {
        skipSelectedHotelHeader: true,
      },
    );
  }

  createAccessGroup(
    payload: CreateAdminAccessGroupDto,
  ): Promise<AdminAccessGroupDetail> {
    return this.request<AdminAccessGroupDetail>(this.basePath, {
      method: "POST",
      body: JSON.stringify(payload),
      skipSelectedHotelHeader: true,
    });
  }

  updateAccessGroup(
    id: string,
    payload: UpdateAccessGroupDto,
  ): Promise<AdminAccessGroupSummary> {
    return this.request<AdminAccessGroupSummary>(`${this.basePath}/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
      skipSelectedHotelHeader: true,
    });
  }

  deleteAccessGroup(id: string): Promise<void> {
    return this.request<void>(`${this.basePath}/${id}`, {
      method: "DELETE",
      skipSelectedHotelHeader: true,
    });
  }

  replaceAccessGroupMembers(
    id: string,
    userIds: string[],
  ): Promise<AdminAccessGroupDetail> {
    return this.request<AdminAccessGroupDetail>(
      `${this.basePath}/${id}/members`,
      {
        method: "PUT",
        body: JSON.stringify({ userIds }),
        skipSelectedHotelHeader: true,
      },
    );
  }

  addAccessGroupMember(id: string, userId: string): Promise<void> {
    return this.request<void>(`${this.basePath}/${id}/members/${userId}`, {
      method: "POST",
      skipSelectedHotelHeader: true,
    });
  }

  removeAccessGroupMember(id: string, userId: string): Promise<void> {
    return this.request<void>(`${this.basePath}/${id}/members/${userId}`, {
      method: "DELETE",
      skipSelectedHotelHeader: true,
    });
  }

  replaceAccessGroupTenants(
    id: string,
    tenantIds: string[],
  ): Promise<AdminAccessGroupDetail> {
    return this.request<AdminAccessGroupDetail>(
      `${this.basePath}/${id}/tenants`,
      {
        method: "PUT",
        body: JSON.stringify({ tenantIds }),
        skipSelectedHotelHeader: true,
      },
    );
  }

  addAccessGroupTenant(id: string, tenantId: string): Promise<void> {
    return this.request<void>(`${this.basePath}/${id}/tenants/${tenantId}`, {
      method: "POST",
      skipSelectedHotelHeader: true,
    });
  }

  removeAccessGroupTenant(id: string, tenantId: string): Promise<void> {
    return this.request<void>(`${this.basePath}/${id}/tenants/${tenantId}`, {
      method: "DELETE",
      skipSelectedHotelHeader: true,
    });
  }

  getUserAccessGroups(userId: string): Promise<UserAdminAccessGroupSummary[]> {
    return this.get<UserAdminAccessGroupSummary[]>(
      `/admin/users/${userId}/access-groups`,
      undefined,
      {
        skipSelectedHotelHeader: true,
      },
    );
  }
}

export const adminAccessGroupsService = new AdminAccessGroupsService();
